import { shouldPolyfill } from "@formatjs/intl-datetimeformat/should-polyfill.js";

const localeDataModules = {
  // Vite import-glob patterns must start with '/' or './'.
  // Using a root-relative path lets us glob the package files in node_modules.
  ...import.meta.glob("/node_modules/@formatjs/intl-datetimeformat/locale-data/*.js"),
  ...import.meta.glob("/node_modules/@formatjs/intl-datetimeformat/locale-data/*.mjs"),
} as Record<string, () => Promise<unknown>>;

function findLocaleDataImporter(localeTag: string): (() => Promise<unknown>) | undefined {
  const candidates = new Set<string>();

  candidates.add(localeTag);
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const canonical = (Intl as any).getCanonicalLocales(localeTag)[0] as string | undefined;
    if (canonical) {
      candidates.add(canonical);
    }
  } catch {
    // ignore
  }

  // Common fallbacks for region-specific tags (e.g. fr-HT -> fr)
  for (const tag of Array.from(candidates)) {
    const base = tag.split("-")[0];
    if (base && base !== tag) {
      candidates.add(base);
    }
  }

  // Search the globbed module keys by suffix to avoid relying on exact key format.
  for (const tag of candidates) {
    const jsSuffix = `/locale-data/${tag}.js`;
    const mjsSuffix = `/locale-data/${tag}.mjs`;

    for (const key of Object.keys(localeDataModules)) {
      if (key.endsWith(jsSuffix) || key.endsWith(mjsSuffix)) {
        return localeDataModules[key];
      }
    }
  }

  return undefined;
}

export const shouldPolyfillLocale = async (locale: string) => {
  const unsupportedLocale = shouldPolyfill(locale);
  // This locale is supported
  if (!unsupportedLocale) return;

  // Load the polyfill 1st BEFORE loading data
  await import("@formatjs/intl-datetimeformat/polyfill-force.js");

  // Parallelize CLDR data loading
  const localeImporter = findLocaleDataImporter(unsupportedLocale);
  if (!localeImporter) {
    // Don't hard-fail the app if we don't have locale-data for a specific region tag.
    // The polyfill is still applied, and Intl will typically fall back to a base locale.
    // This also avoids runtime errors like:
    // Failed to resolve module specifier '@formatjs/intl-datetimeformat/locale-data/fr-HT'
    console.warn(`No @formatjs locale-data module found for '${unsupportedLocale}' (requested locale: '${locale}').`);
    await import("@formatjs/intl-datetimeformat/add-all-tz.js");
    return;
  }

  await Promise.all([import("@formatjs/intl-datetimeformat/add-all-tz.js"), localeImporter()]);
};
